import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import { AxiosError } from "axios"
import {
  type Body_login_login_access_token as AccessToken,
  type ApiError,
  LoginService,
  OpenAPI,
  type UserPublic,
  type UserRegister,
  UsersService,
} from "../client"
import useCustomToast from "./useCustomToast"

const isLoggedIn = () => {
  return localStorage.getItem("access_token") !== null
}

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { showToast, showErrorToast } = useCustomToast()
  const queryClient = useQueryClient()
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery<UserPublic | null, Error>({
    queryKey: ["currentUser"],
    queryFn: UsersService.readUserMe,
    enabled: isLoggedIn(),
    retry: false,
  })

  useEffect(() => {
    if (isError && isLoggedIn()) {
      localStorage.removeItem("access_token")
      navigate({ to: "/login" })
    }
  }, [isError])

  const signUpMutation = useMutation({
    mutationFn: (data: UserRegister) =>
      UsersService.registerUser({ requestBody: data }),

    onSuccess: () => {
      navigate({ to: "/login" })
      showToast(
        "Account created.",
        "Your account has been created successfully.",
        "success",
      )
    },
    onError: (err: ApiError) => {
      let errDetail = (err.body as any)?.detail

      if (err instanceof AxiosError) {
        errDetail = err.message
      }

      showErrorToast(errDetail)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })

  const login = async (data: AccessToken) => {
    const response = await LoginService.loginAccessToken({
      formData: data,
    })
    // The access-token endpoint returns Token | TwoSvChallenge. This hook's
    // login() is not used by the login screen (src/routes/login.tsx owns the
    // real password + 2SV flow via raw fetch); narrow to the token case so the
    // regenerated union compiles, and refuse a challenge here explicitly rather
    // than storing undefined.
    if (!("access_token" in response)) {
      throw new Error("Two-step verification required")
    }
    localStorage.setItem("access_token", response.access_token)
  }

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      navigate({ to: "/" })
    },
    onError: (err: ApiError) => {
      let errDetail = (err.body as any)?.detail

      if (err instanceof AxiosError) {
        errDetail = err.message
      }

      if (Array.isArray(errDetail)) {
        errDetail = "Something went wrong"
      }

      setError(errDetail)
    },
  })

  const logout = () => {
    localStorage.removeItem("access_token")
    navigate({ to: "/login" })
  }

  const recoverPassword = useMutation({
    mutationFn: async (data: { email: string }) => {
      // POST /api/v1/password-recovery/{email}. The backend route is
      // include_in_schema=False, so the generated client no longer exposes
      // LoginService.recoverPassword. Call it directly, mirroring login.tsx.
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/password-recovery/${encodeURIComponent(data.email)}`,
        { method: "POST" },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body?.detail as string) || "Password recovery failed")
      }
    },
    onSuccess: () => {
      showToast(
        "Email sent",
        "Check your email for password recovery instructions.",
        "success",
      )
    },
    onError: (err: ApiError) => {
      let errDetail = (err.body as any)?.detail
      if (err instanceof AxiosError) {
        errDetail = err.message
      }
      showErrorToast(errDetail)
    },
  })

  return {
    signUpMutation,
    loginMutation,
    logout,
    user,
    isLoading,
    error,
    resetError: () => setError(null),
    recoverPassword,
  }
}

export { isLoggedIn }
export default useAuth
